import React, { useCallback, useEffect, useState } from 'react';
import { canReadClaims } from '../utils/roles';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../contexts/authContext';
import {
  Box,
  Container,
  FormControl,
  Select,
  MenuItem,
} from '@material-ui/core';
import TenantSelector from '../components/TenantSelector';
import { MDTypography } from '../components/MDTypography';
import {
  approveClaim,
  getAllClaims,
  approveBill,
  rejectBill,
  submitBillVerdict,
  requestBulkDownload,
  BulkDownloadConfig,
  BulkDownloadResponse,
} from '../services/claimsService';
import { BillVerdictStatus, Severity } from '../types';
import useSnackbar from '../hooks/useSnackbar';
import { ClaimTimeLinePage } from './Claims/ClaimTimeLinePage';
import { FFTable, FFTableColumn } from '../components/FFTable';
import {
  ListFilterBar,
  ListFilters,
  PAST_DATE_OPTIONS,
  SelectFilter,
} from '../components/ListFilterBar/ListFilterBar';
import { MDButton } from 'components/MDButton';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { ClaimWithBills, BillAction } from './Claims/ClaimDetailsTypes';
import ClaimExpandableContent from './Claims/ClaimExpandableContent';
import { BulkDownloadDialog } from '../components/BulkDownload/BulkDownloadDialog';
import { AxiosError } from 'axios';

type ClaimsPageData = {
  entities: ClaimWithBills[];
  isLoading: boolean;
  error: null | Error;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
};

type QueryState = {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
};

type CasesFilters = ListFilters & { status: string; claimType: string };

// Cases open on the claims still being worked.
const DEFAULT_CASES_FILTERS: CasesFilters = {
  search: '',
  status: 'Open',
  claimType: '',
  datePreset: 'all',
};

const CASES_SELECTS: SelectFilter[] = [
  {
    key: 'status',
    id: 'cases-status',
    label: 'Claim Status',
    options: [
      { value: 'Open', label: 'Open' },
      { value: 'Pending', label: 'Pending' },
      { value: 'Closed', label: 'Closed' },
      { value: 'Denied', label: 'Denied' },
      { value: '', label: 'All statuses' },
    ],
  },
  {
    key: 'claimType',
    id: 'cases-claim-type',
    label: 'Claim Type',
    options: [
      { value: '', label: 'All claim types' },
      { value: 'Outpatient', label: 'Outpatient' },
      { value: 'Inpatient', label: 'Inpatient' },
      { value: 'Other', label: 'Other' },
    ],
  },
];

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const listQuery = (f: CasesFilters) => ({
  fromDate: f.fromDate,
  untilDate: f.untilDate,
  status: f.status || undefined,
  filters: { search: f.search, claimType: f.claimType },
});

export default function ClaimsPage() {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const [showClaimPage, setShowClaimPage] = useState(false);
  const [claimId, setClaimId] = useState<string | number>('');
  const [inProgress, setInProgress] = useState(false);
  const [pageData, setPageData] = useState<ClaimsPageData>({
    entities: [],
    isLoading: true,
    error: null,
    pagination: {
      page: 1,
      pageSize: 100,
      total: 0,
      totalPages: 0,
    },
  });
  const [queryState, setQueryState] = useState<QueryState>({
    page: 1,
    pageSize: 100,
    sortBy: 'totalBilledAmount',
    sortOrder: 'desc',
  });
  const [filters, setFilters] = useState<CasesFilters>(DEFAULT_CASES_FILTERS);
  // Only the first load replaces the page with a loading message; after that
  // the filter bar stays mounted so a search never loses its cursor.
  const [hasLoaded, setHasLoaded] = useState(false);
  const show = useSnackbar();
  const [currentTenant, setCurrentTenant] = useState<number>(0);
  const [showBulkDownloadDialog, setShowBulkDownloadDialog] = useState(false);

  const { role } = user || { role: '' };

  const columns: FFTableColumn<ClaimWithBills>[] = [
    {
      id: 'caseId',
      label: t('Case Id'),
      width: 110,
      type: 'text',
      // sortable: true,
      filterable: true,
    },
    {
      id: 'policyNumber',
      label: t('Policy Number'),
      type: 'text',
      // sortable: true,
      filterable: true,
    },
    // {
    //   id: 'status',
    //   label: t('Status'),
    //   width: 100,
    //   type: 'text',
    //   sortable: true,
    //   filterable: true,
    // },
    {
      id: 'patientType',
      label: t('Patient Type'),
      width: 100,
      type: 'text',
      sortable: true,
      filterable: true,
    },
    {
      id: 'dateOfService',
      label: t('Date of Service'),
      type: 'date',
      sortable: true,
    },
    {
      id: 'totalBilledAmount',
      label: 'Billed Amount',
      type: 'text',
      sortable: true,
      renderCell(value, row) {
        return <span>{formatter.format(value)}</span>;
      },
    },
    // {
    //   id: 'totalAllowedAmount',
    //   label: 'Allowed Amount',
    //   type: 'text',
    //   sortable: true,
    //   renderCell(value, row) {
    //     return <span>{formatter.format(value)}</span>;
    //   },
    // },
    {
      id: 'patientName',
      label: 'Patient Name',
      type: 'text',
    },
    {
      type: 'custom',
      id: 'medicalRecordsStatus',
      label: t('Medical Documents Status'),
      renderCell: (value, row) => (
        <span>
          {row.hipaaWaiverFileUrls && row.medicalRecordFileUrls && row.medicalRecordFileUrls.length > 0
            ? t('Available')
            : t('N/A')}
        </span>
      ),
    },
    {
      id: 'billsStatusText',
      label: t('Bills Status'),
      width: 150,
      type: 'text',
      renderCell: (value, row) => {
        return (
          <span
            style={{
              color: row?.billsStatus === 'completed' ? 'green' : 'orange',
            }}
          >
            {value}
          </span>
        );
      },
    },
    {
      id: 'billId',
      label: t('Bill Id'),
      type: 'text',
      filterable: true,
      hide: true,
    },
    // {
    //   type: 'action',
    //   id: 'detailsAction',
    //   label: 'Details',
    // },
    // {
    //   type: 'action',
    //   id: 'approveAction',
    //   label: 'Approve',
    //   shouldDisableCta: (row) =>
    //     inProgress ||
    //     !(row.status === 'In Progress' || row.status === 'Pending'),
    // },
  ];

  const fetchDataWithQuery = useCallback(
    async (query: QueryState) => {
      try {
        setPageData((prev) => ({ ...prev, isLoading: true }));

        const response = await getAllClaims(
          {
            page: query.page,
            pageSize: query.pageSize,
            sortBy: query.sortBy,
            sortOrder: query.sortOrder,
            ...listQuery(filters),
          },
          currentTenant
        );

        setPageData({
          entities: response.claims,
          isLoading: false,
          error: null,
          pagination: response.pagination,
        });
        setHasLoaded(true);

        // Update the query state with the actual values used
        setQueryState(query);
      } catch (error) {
        const e = error as Error;
        setPageData((prev) => ({
          ...prev,
          isLoading: false,
          error: e,
        }));
        show('Failed to load claims data', Severity.error);
      }
    },
    [show, filters, currentTenant]
  );

  // Any change to what is being asked for starts again from the first page.
  // Keyed on the inputs rather than the callback, whose identity follows the
  // snackbar's `show` and would otherwise reload after every message.
  useEffect(() => {
    fetchDataWithQuery({ ...queryState, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, currentTenant]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      const newQuery: QueryState = {
        ...queryState,
        page: newPage,
      };
      fetchDataWithQuery(newQuery);
    },
    [queryState, fetchDataWithQuery]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      const newQuery: QueryState = {
        ...queryState,
        page: 1, // Reset to first page when changing page size
        pageSize: newPageSize,
      };
      fetchDataWithQuery(newQuery);
    },
    [queryState, fetchDataWithQuery]
  );

  const onActionClickHandler = useCallback(
    async (claimId: string | number, action: string) => {
      if (claimId) {
        setClaimId(claimId);
      }
      if (action === 'detailsAction') {
        setShowClaimPage(true);
      } else if (action === 'approveAction') {
        try {
          const row = pageData.entities.find(
            (r) => r.fairFareInternalClaimId === claimId
          );
          if (!row) {
            return;
          }
          setInProgress(true);
          await approveClaim(claimId);
          setPageData((prev) => ({
            ...prev,
            entities: [
              ...prev.entities.filter(
                (r) => r.fairFareInternalClaimId !== claimId
              ),
              { ...row, status: 'Approved' },
            ],
          }));
          show('Claim Approved', Severity.success);
        } catch (error) {
          show('Approve Claim Failed', Severity.warning);
        }
        setInProgress(false);
      }
    },
    [pageData.entities, show]
  );

  const handleCloseClaimPage = useCallback(() => {
    setShowClaimPage(false);
  }, []);

  const handleBillAction = useCallback(
    async (billId: number, action: BillAction) => {
      try {
        setInProgress(true);

        if (action === 'approve') {
          await approveBill(billId);
          show('Bill Approved', Severity.success);
        } else if (action === 'reject') {
          await rejectBill(billId);
          show('Bill Rejected', Severity.success);
        }

        // Refresh the claims data to get updated bill statuses
        await fetchDataWithQuery(queryState);
      } catch (error) {
        show(
          `${action === 'approve' ? 'Approve' : 'Reject'} Bill Failed`,
          Severity.error
        );
      } finally {
        setInProgress(false);
      }
    },
    [show, queryState, fetchDataWithQuery]
  );

  const handleBillVerdict = useCallback(
    async (
      billId: number,
      verdict: BillVerdictStatus,
      amount: number,
      reason: string
    ) => {
      try {
        setInProgress(true);

        // Find the bill to get gemBillNo
        let gemBillNo = '';
        const currentClaim = pageData.entities.find((claim) => {
          const claimWithBills = claim as any as ClaimWithBills;
          const bill = claimWithBills.bills?.find((b) => b.id === billId);
          if (bill) {
            gemBillNo = bill.gemBillNo;
            return true;
          }
          return false;
        });

        if (!gemBillNo) {
          show('Bill not found', Severity.error);
          return;
        }

        const claimId = (currentClaim as any).fairFareInternalClaimId;

        await submitBillVerdict(claimId, {
          verdict,
          amount,
          billId: gemBillNo,
          reason,
        });

        const actionText =
          verdict === 'approved'
            ? 'Bill Approved'
            : verdict === 'partial-approved'
            ? 'Bill Partially Approved'
            : 'Bill Rejected';

        show(actionText, Severity.success);

        // Refresh the claims data to get updated bill statuses
        await fetchDataWithQuery(queryState);
      } catch (error) {
        const actionText = verdict === 'rejected' ? 'Reject' : 'Approve';
        show(`${actionText} Bill Failed`, Severity.error);
      } finally {
        setInProgress(false);
      }
    },
    [show, queryState, fetchDataWithQuery, pageData.entities]
  );

  const handleDownloadExcel = async () => {
    // The export is what is on screen: same search, filters and tenant.
    const response = await getAllClaims(
      {
        page: 1,
        pageSize: 10000, // A large number to fetch all data
        ...listQuery(filters),
      },
      currentTenant
    );
    const headers = columns
      .filter((x) => x.id !== 'detailsAction' && x.id !== 'approveRejectAction')
      .map((col) => ({ id: col.id, label: col.label }));
    const res: any[] = [];
    response.claims.forEach((claim: any) => {
      const row: any = {};
      headers.forEach((header) => {
        row[header.label] = claim[header.id];
      });
      res.push(row);
    });
    const worksheet = XLSX.utils.json_to_sheet(res);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Claims');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, 'claims-data.xlsx');
  };

  const handleBulkDownloadClick = () => {
    setShowBulkDownloadDialog(true);
  };

  const handleBulkDownloadClose = () => {
    setShowBulkDownloadDialog(false);
  };

  const handleBulkDownloadSubmit = async (
    config: BulkDownloadConfig
  ): Promise<BulkDownloadResponse> => {
    try {
      const response = await requestBulkDownload(config);
      show(
        'Bulk download completed successfully! Download will start automatically.',
        Severity.success
      );
      return response;
    } catch (error) {
      const errors =
        error instanceof AxiosError ? error.response?.data?.errors || [] : [];
      const errorMessage = errors
        .map((x: { message: string }) => x.message)
        .join(', ');
      show(errorMessage, Severity.error);
      throw new Error(errorMessage);
    }
  };

  // Check if user has permission for bulk download (admin or super_user only)
  const canAccessBulkDownload = canReadClaims(role);

  if (!user) {
    return <div>{t('Loading')}..</div>;
  }

  if (user.status === 'new') {
    return (
      <Container>
        <MDTypography variant="h4" component="p">
          Welcome to FairFare Dashboard!
        </MDTypography>
        <MDTypography component="p">
          To activate your account and access all FairFare features please
          contact us
        </MDTypography>
      </Container>
    );
  }

  if (!hasLoaded && pageData.isLoading) {
    return <div>{t('Loading')}..</div>;
  }

  if (pageData.error) {
    return (
      <div>
        {t('Error loading claims data')}: {pageData.error.message}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '100vw', margin: '0 auto', overflow: 'auto' }}>
      <Box display="flex" justifyContent="flex-end" style={{ gap: '8px' }}>
        <MDButton variant="gradient" color="info" onClick={handleDownloadExcel}>
          {t('Download as Excel')}
        </MDButton>
        {canAccessBulkDownload && (
          <MDButton
            variant="gradient"
            color="success"
            onClick={handleBulkDownloadClick}
          >
            {t('Bills Bulk Download')}
          </MDButton>
        )}
      </Box>
      <ListFilterBar
        filters={filters}
        defaults={DEFAULT_CASES_FILTERS}
        onChange={setFilters}
        total={pageData.pagination.total}
        noun="claims"
        searchPlaceholder="Search by case ID, policy number, bill ID, claim number or patient"
        selects={CASES_SELECTS}
        dateLabel="Date of Service"
        dateOptions={PAST_DATE_OPTIONS}
        tenantSlot={
          role === 'super_user' ? (
            <TenantSelector
              value={currentTenant}
              onChange={setCurrentTenant}
              label={t('Tenant')}
              compact
            />
          ) : undefined
        }
      />
      <FFTable
        columns={columns}
        rows={pageData.entities}
        onActionClick={onActionClickHandler}
        pagination={{
          page: pageData.pagination.page,
          pageSize: pageData.pagination.pageSize,
          total: pageData.pagination.total,
          totalPages: pageData.pagination.totalPages,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange,
          pageSizeOptions: [50, 100, 200],
        }}
        serverSide={true}
        loading={pageData.isLoading}
        error={pageData.error}
        onSortChange={(orderBy, order) => {
          const mappedValue = orderBy === 'status' ? 'claimStatus' : orderBy;

          const newQuery: QueryState = {
            ...queryState,
            page: 1, // Reset to first page when sorting changes
            sortBy: mappedValue,
            sortOrder: order,
          };

          fetchDataWithQuery(newQuery);
        }}
        initialSort={{
          orderBy:
            queryState.sortBy === 'claimStatus'
              ? 'status'
              : queryState.sortBy || 'id',
          order: queryState.sortOrder || 'asc',
        }}
        rowKey="fairFareInternalClaimId"
        emptyStateMessage={t('No claims records found')}
        errorStateMessage={t('Error loading claims data')}
        loadingStateMessage={t('Loading claims data...')}
        expandableRows={{
          isRowExpandable: (row) => {
            return row?.bills?.length > 0 
            || (row.medicalRecordFileUrls !== undefined && row.medicalRecordFileUrls?.length > 0) 
            || (row.hipaaWaiverFileUrls !== undefined && row.hipaaWaiverFileUrls?.length > 0);
          },
          renderExpandedContent: (row) => {
            return (
              <ClaimExpandableContent
                claim={row}
                onBillAction={handleBillAction}
                onBillVerdict={handleBillVerdict}
                loading={inProgress}
                hipaaWaiverFileUrls={row.hipaaWaiverFileUrls}
                medicalRecordFileUrls={row.medicalRecordFileUrls}
                hipaaWaiverRequest={row.hipaaWaiverRequest}
              />
            );
          },
        }}
      />
      {claimId && (
        <ClaimTimeLinePage
          claimId={claimId.toString()}
          onClose={handleCloseClaimPage}
          isVisible={showClaimPage}
        />
      )}
      <BulkDownloadDialog
        open={showBulkDownloadDialog}
        onClose={handleBulkDownloadClose}
        onDownload={handleBulkDownloadSubmit}
      />
    </div>
  );
}
